/**
 * Back In Stock Form Component
 * 
 * Handles Klaviyo Back In Stock subscriptions for out-of-stock product variants.
 * 
 * Note: This implementation uses Klaviyo's Back In Stock API. You may need to:
 * 1. Configure a Klaviyo Public API Key in theme settings, OR
 * 2. Set up a server-side proxy endpoint if the API requires a Private API Key
 * 
 * The Klaviyo Public API Key can be found in your Klaviyo account settings.
 */
class BackInStock extends HTMLElement {
    private form: HTMLFormElement | null = null;
    private emailInput: HTMLInputElement | null = null;
    private submitButton: HTMLButtonElement | null = null;
    private messageContainer: HTMLElement | null = null;
    private variantId: string | null = null;
    private productId: string | null = null;
    private productTitle: string | null = null;
    private variantTitle: string | null = null;

    constructor() {
        super();
    }

    connectedCallback() {
        this.init();
    }

    private init(): void {
        this.variantId = this.dataset.variantId || null;
        this.productId = this.dataset.productId || null;
        this.productTitle = this.dataset.productTitle || null;
        this.variantTitle = this.dataset.variantTitle || null;

        this.form = this.querySelector('.back-in-stock-form') as HTMLFormElement;
        this.emailInput = this.querySelector('.back-in-stock-form__email') as HTMLInputElement;
        this.submitButton = this.querySelector('.back-in-stock-form__submit') as HTMLButtonElement;
        this.messageContainer = this.querySelector('.back-in-stock-form__message') as HTMLElement;

        if (this.form && this.emailInput && this.submitButton) {
            this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
    }

    private async handleSubmit(event: Event): Promise<void> {
        event.preventDefault();
        event.stopPropagation();

        if (!this.emailInput || !this.submitButton || !this.variantId || !this.productId) {
            return;
        }

        const email = this.emailInput.value.trim();

        if (!this.validateEmail(email)) {
            this.showMessage('Please enter a valid email address.', 'error');
            return;
        }

        this.setLoadingState(true);

        try {
            await this.subscribeToBackInStock(email);
            this.showSuccessState();
            this.emailInput.value = '';
        } catch (error) {
            console.error('Error subscribing to back in stock:', error);
            this.showMessage('Something went wrong. Please try again later.', 'error');
            this.setLoadingState(false);
        }
    }

    private validateEmail(email: string): boolean {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    private async subscribeToBackInStock(email: string): Promise<void> {
        if (!this.variantId || !this.productId) {
            throw new Error('Missing variant or product ID');
        }

        const klaviyoPublicApiKey = this.getKlaviyoPublicApiKey();

        const variantIdentifier = `$shopify:::$default:::${this.variantId}`;

        const payload = {
            data: {
                type: 'back-in-stock-subscription',
                attributes: {
                    profile: {
                        data: {
                            type: 'profile',
                            attributes: {
                                email: email
                            }
                        }
                    },
                    channels: ['EMAIL']
                },
                relationships: {
                    variant: {
                        data: {
                            type: 'catalog-variant',
                            id: variantIdentifier
                        }
                    }
                }
            }
        };

        const response = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${klaviyoPublicApiKey}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'revision': '2023-12-15'
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.errors?.[0]?.detail || errorData.detail || `HTTP error! status: ${response.status}`;
            throw new Error(errorMessage);
        }
    }

    private getKlaviyoPublicApiKey(): string {
        return 'Tcu7BE';
    }

    private setLoadingState(isLoading: boolean): void {
        if (this.submitButton) {
            this.submitButton.disabled = isLoading;
            if (isLoading) {
                this.submitButton.textContent = 'Subscribing...';
            } else {
                this.submitButton.textContent = 'Notify Me';
            }
        }
        if (this.emailInput) {
            this.emailInput.disabled = isLoading;
        }
    }

    private showSuccessState(): void {
        if (this.submitButton) {
            this.submitButton.textContent = 'Thank You!';
            this.submitButton.disabled = true;
        }
        if (this.emailInput) {
            this.emailInput.disabled = true;
        }
        this.showMessage('You\'ll be notified when this product is back in stock!', 'success');
    }

    private showMessage(message: string, type: 'success' | 'error'): void {
        if (!this.messageContainer) {
            return;
        }

        this.messageContainer.textContent = message;
        this.messageContainer.className = `back-in-stock-form__message back-in-stock-form__message--${type}`;
        this.messageContainer.setAttribute('role', 'status');
        this.messageContainer.setAttribute('aria-live', 'polite');

        setTimeout(() => {
            if (this.messageContainer) {
                this.messageContainer.textContent = '';
                this.messageContainer.className = 'back-in-stock-form__message';
            }
        }, 5000);
    }

    public updateVariant(variantId: string, productId: string, productTitle: string, variantTitle: string): void {
        this.variantId = variantId;
        this.productId = productId;
        this.productTitle = productTitle;
        this.variantTitle = variantTitle;

        if (this.dataset) {
            this.dataset.variantId = variantId;
            this.dataset.productId = productId;
            this.dataset.productTitle = productTitle;
            this.dataset.variantTitle = variantTitle;
        }

        if (this.emailInput) {
            this.emailInput.value = '';
        }

        if (this.messageContainer) {
            this.messageContainer.textContent = '';
            this.messageContainer.className = 'back-in-stock-form__message';
        }
    }
}

export default BackInStock;
